import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { TreeNode } from 'primeng/api';
import { IcdCodeService } from '../services/icd-code.service';
import { debounceTime, Subject, Subscription, tap } from 'rxjs';
import { CorePopupComponent } from '../../../../ui-components/core-popup/core-popup.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-icd-codes-popup',
  templateUrl: './icd-codes-popup.component.html',
  styleUrl: './icd-codes-popup.component.scss',
})
export class IcdCodesPopupComponent implements OnInit, OnDestroy {
  @ViewChild('popup') popup!: CorePopupComponent;
  @Output() saveOn = new EventEmitter<any[]>();
  @Input() showOftenUsage: boolean = true;

  icdCodeList: TreeNode[] = [];
  oftenIcdCodeList: TreeNode[] = [];
  filteredIcdCodes: TreeNode[] = [];
  selectedIcdCodes: TreeNode[] = [];

  selectedFilter: 'all' | 'oftenUsage' = 'all';
  searchText: string = '';
  systemLanguage: string = 'tr';

  loading: boolean = false;
  searchLoading: boolean = false;

  private searchTerm$ = new Subject<string>();
  private subscriptions = new Subscription();

  private isLoaded = false;
  private isOftenLoaded = false;

  constructor(
    private readonly icdCodeService: IcdCodeService,
    private readonly translateService: TranslateService
  ) { }

  ngOnInit(): void {
    this.systemLanguage =
      this.translateService.currentLang ||
      this.translateService.getDefaultLang() ||
      'tr';

    this.initSearchListener();
    // this.loadIcdCodes();
    // this.loadOftenUsedCodes();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  /* ------------------ DATA LOAD ------------------ */

  private loadIcdCodes(): void {
    this.loading = true;
    this.icdCodeService.getTreeList().subscribe({
      next: (res: TreeNode[]) => {
        this.icdCodeList = res ?? [];
        this.filteredIcdCodes = this.icdCodeList.slice(0, 100);
        this.isLoaded = true;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }

  private loadOftenUsedCodes(): void {
    this.icdCodeService.getICDGroupList().subscribe({
      next: (res: any[]) => {
        this.oftenIcdCodeList =
          res?.map((group) => ({
            key: `${group.id}-group`,
            label: group.name,
            data: {},
            leaf: false,
            children:
              group.icdCodes?.map((code: any) => ({
                key: code.id?.toString(),
                label: code.name,
                data: {
                  id: code.id,
                  code: code.code,
                },
                leaf: true,
                children: [],
              })) ?? [],
          })) ?? [];

        this.isOftenLoaded = true;
      },
    });
  }
  /* ------------------ SEARCH ------------------ */

  private initSearchListener(): void {
    const sub = this.searchTerm$
      .pipe(
        tap(() => (this.searchLoading = true)),
        debounceTime(900),
        tap(() => (this.searchLoading = false))
      )
      .subscribe((term) => {
        const source =
          this.selectedFilter === 'all'
            ? this.icdCodeList
            : this.oftenIcdCodeList;

        this.filteredIcdCodes = this.filterTree(term, source);
      });

    this.subscriptions.add(sub);
  }

  onSearch(value: string): void {
    this.searchTerm$.next(value);
  }

  filterTree(searchTerm: string, nodes: TreeNode[]): TreeNode[] {
    if (!searchTerm?.trim()) {
      // return [...nodes];
      return nodes.slice(0, 100);
    }

    const lowerSearch = searchTerm.toLocaleLowerCase(this.systemLanguage);

    return nodes
      .map((node) => {
        if (node.children?.length) {
          const filteredChildren = this.filterTree(
            searchTerm, 
            node.children);

          if (filteredChildren.length) {
            return {
              ...node,
              children: filteredChildren,
              expanded: true,
            };
          }
        }

        const label = node.label?.toLocaleLowerCase(this.systemLanguage) ?? '';
        const code = node.data?.code?.toLocaleLowerCase(this.systemLanguage) ?? '';

        if (node.leaf && (label.includes(lowerSearch) || code.includes(lowerSearch))) {
          return { ...node };
        }

        return null;
      })
      .filter((node): node is TreeNode => node !== null)
      .slice(0, 100);
  }

  /* ------------------ TREE ACTIONS ------------------ */

  onNodeClick(node: TreeNode, event: MouseEvent): void {
    if (node.leaf) {
      const index = this.selectedIcdCodes.findIndex(
        (n) => n.key === node.key
      );

      index === -1
        ? this.selectedIcdCodes.push(node)
        : this.selectedIcdCodes.splice(index, 1);
    } else {
      node.expanded = !node.expanded;
    }

    event.stopPropagation();
  }

  isNodeSelected(node: TreeNode): boolean {
    return this.selectedIcdCodes.some((n) => n.key === node.key);
  }

  deleteIcd(icdNode: TreeNode): void {
    this.selectedIcdCodes = this.selectedIcdCodes.filter(
      (code) => code.data?.id !== icdNode.data?.id
    );
  }

  /* ------------------ RADIO FILTER ------------------ */

  onRadioChange(): void {
    this.searchText = '';

    this.filteredIcdCodes =
      this.selectedFilter === 'all'
        ? this.icdCodeList.slice(0, 100)
        : this.oftenIcdCodeList;
  }

  /* ------------------ POPUP ------------------ */

  initilize(): void {
    this.popup.openDialog();
    if (!this.isLoaded) {
    this.loadIcdCodes();
  }

  if (this.showOftenUsage && !this.isOftenLoaded) {
    this.loadOftenUsedCodes();
  }

  }

  onPopupClosed(): void {
    this.selectedIcdCodes = [];
    // this.filteredIcdCodes = [...this.icdCodeList];
    this.filteredIcdCodes = this.icdCodeList.slice(0, 100);
    this.searchText = '';
    this.selectedFilter = 'all';
  }

  closePopup(): void {
    this.popup.closeDialog();
  }

  save(): void {
    const response =
      this.selectedIcdCodes?.map((code) => ({
        id: code.data?.id,
        code: code.data?.code,
        name: code.label,
      })) ?? [];

    this.saveOn.emit(response);
    this.closePopup();
  }
}
